"use client";
import log from "logbeacon";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "utils";
import { useMemoizedFn } from "ahooks";
import { AgentTopTab } from "../AgentTopTab";
import { Thinking } from "../Thinking";
import { SummaryTimeLine, SummaryTimeLineRef } from "./SummaryTimeLine";
import Translations from "@translate/components/CoPilot/CopilotAgent/SummaryAgent/SummaryAgent";
import { I18nUtil } from "@utils/i18n";
import { CheckBoxGroup } from "@/src/components/CheckBoxGroup";
import NextImage from "@/src/components/NextImage";
import { useAgentReportList } from "@/src/hooks/useAgentReportList";
import {
  AgentReport,
  AgentReportCategoryEnum,
  AgentReportLevelEnum,
} from "@/src/features/api/memberApi";
import {
  InsertConfig,
  WSMessageFilter,
} from "@/src/hooks/useAgentReportInsertion";

import { WSAgentReportMessageData } from "@/src/features/api/generalWS";
import {
  NOTI_AGENT_REPORT_HANDLER_ID,
  useWebSocketNotificationContext,
  wsLevelFilter,
  defaultMemberPlanInfoFilter,
} from "@/src/features";

const I18n = I18nUtil.createScoped(Translations);

interface SummaryAgentProps {
  className?: string;
  agentId: string;
  onChartClick: (value: {
    question?: string;
    title?: string;
    desc?: string;
    id?: string;
    sourceLink?: string;
  }) => void;
  category: string[];
}

const wsInsertConfig: InsertConfig = {
  position: "after",
  matcher: (
    item: AgentReport,
    index: number,
    _groupKey: string,
    groupIndex: number,
  ) => {
    return groupIndex === 0 && item.type === "ALERT_SUMMARY" && index === 0;
  },
  searchGroups: 1,
};

export const SummaryAgent = ({
  className,
  agentId,
  category,
  onChartClick,
}: SummaryAgentProps) => {
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const options = [
    { label: I18n.t("Stocks"), value: AgentReportCategoryEnum.STOCK },
    { label: I18n.t("Crypto"), value: AgentReportCategoryEnum.CRYPTO },
  ];
  const summaryTimeLineRef = useRef<SummaryTimeLineRef>(null);

  // 处理 category 筛选条件：如果只选择了一个 category，传递该值；如果选择多个或为空，不传递
  const categoryValue = useMemo(() => {
    log.debug("[文案]categoryValue", selectedValues);
    if (!selectedValues || selectedValues.length === 0) {
      return undefined;
    }
    // 如果只选择了一个 category，传递该值进行筛选
    if (selectedValues.length === 1) {
      return selectedValues[0] as AgentReportCategoryEnum;
    }
    // 如果选择了多个 category，不传递 category 参数（获取所有 category 的数据）
    return undefined;
  }, [selectedValues]);
  const {
    data: agentReportList,
    hasNextPage,
    isLoading,
    isFetching,
    isPending,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useAgentReportList({
    params: {
      agent_ids: agentId.split(",") as string[],
      levels: [
        AgentReportLevelEnum.HIGH,
        AgentReportLevelEnum.MID,
        AgentReportLevelEnum.LOW,
      ],
      category: categoryValue,
      "enrich.enable": true,
      "enrich.with_asset.enable": true,
      "enrich.with_asset.with_price": true,
    },
  });
  useEffect(() => {
    if (category && category.length > 1) {
      const categories = category
        .map((item: string) => item.trim())
        .filter(Boolean);
      log.debug("[文案]category", category, categories);
      setSelectedValues(categories);
    }
  }, [category]);

  const {
    updateHandler: updateHandlerWebSocketNotification,
    updateFilter: updateFilterWebSocketNotification,
  } = useWebSocketNotificationContext();

  const currAgentIds: string[] = useMemo(() => agentId.split(","), [agentId]);

  // 使用 useCallback 稳定 customFilter 函数引用
  const wsAgentIdFilter: WSMessageFilter = useMemoizedFn(
    (item: WSAgentReportMessageData, context) => {
      log.debug(
        "[wsAgentIdFilter][ws] result:",
        currAgentIds.includes(item?.extParams?.agentId ?? ""),
      );
      return (
        currAgentIds.includes(item?.extParams?.agentId ?? "") &&
        defaultMemberPlanInfoFilter(item, context)
      );
    },
  );

  // 使用 useCallback 稳定 insertData 函数引用
  const handleInsertData = useMemoizedFn((data: AgentReport[]) => {
    if (data?.length > 0) {
      summaryTimeLineRef.current?.insertItems(data, wsInsertConfig);
    }
  });

  useEffect(() => {
    updateFilterWebSocketNotification(
      NOTI_AGENT_REPORT_HANDLER_ID,
      wsAgentIdFilter,
    );
    updateHandlerWebSocketNotification(
      NOTI_AGENT_REPORT_HANDLER_ID,
      (messages) => {
        handleInsertData(messages);
      },
    );

    return () => {
      updateHandlerWebSocketNotification(
        NOTI_AGENT_REPORT_HANDLER_ID,
        undefined,
      );
      updateFilterWebSocketNotification(
        NOTI_AGENT_REPORT_HANDLER_ID,
        wsLevelFilter,
      );
    };
  }, [
    updateHandlerWebSocketNotification,
    updateFilterWebSocketNotification,
    handleInsertData,
    wsAgentIdFilter,
  ]);

  const tabs = [
    { id: "summary", label: I18n.t("Summary"), disabled: false },
    { id: "thinking", label: I18n.t("Thinking"), disabled: false },
  ];
  const [activeTab, setActiveTab] = useState<string>("summary");

  // 当 agentId 变化时，重置 activeTab 为 summary
  useEffect(() => {
    setActiveTab("summary");
  }, [agentId]);

  const handleChange = (_changedValue: string, newValue: string[]) => {
    setSelectedValues(newValue);
  };
  return (
    <div
      className={cn("flex flex-col h-full", className)}
      data-tracking-scope="summary-agent"
    >
      <AgentTopTab
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={(tab: string) => {
          setActiveTab(tab);
          refetch();
        }}
        className="md:mb-4 mb-3"
        rightContent={activeTab === "summary" ? <></> : null}
      />
      {activeTab === "summary" && (
        <div className="md:flex justify-between items-center w-full pb-4 hidden">
          <div className={cn("flex items-center gap-1")}>
            <NextImage
              src="/assets/images/edgen-chat-icon.svg"
              alt="agent-tip"
              width={20}
              height={20}
            />
            <p className={cn("body_bold_l text-content_brand_primary")}>
              {I18n.t("Information for you")}
            </p>
          </div>
          {category.length > 1 && (
            <CheckBoxGroup
              options={options}
              value={selectedValues}
              onChange={handleChange}
            />
          )}
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
        {activeTab === "summary" && (
          <SummaryTimeLine
            key={agentId}
            ref={summaryTimeLineRef}
            agentId={agentId}
            data={agentReportList as AgentReport[]}
            hasNextPage={hasNextPage}
            isLoading={isLoading}
            isFetching={isFetching}
            isPending={isPending}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
            onChartClick={onChartClick}
          />
        )}
        {activeTab === "thinking" && (
          <Thinking
            data={agentReportList as AgentReport[]}
            isLoading={isLoading}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        )}
      </div>
    </div>
  );
};
